import {
  SearchEngine,
  buildResultsPerPage,
  buildSearchEngine,
  getOrganizationEndpoints,
  loadAdvancedSearchQueryActions,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/headless';
import { buildSitesAdvancedQuery } from './sitesAdvancedQuery';
import isCoveoAnalyticsEnabled from '../isCoveoAnalyticsEnabled';

const NATIONWIDE_RESULTS_PER_PAGE = 0;
export const NATIONWIDE_SITES_COUNT_TIMEOUT_MS = 30_000;

// eslint-disable-next-line @typescript-eslint/ban-types
let nationwideCountEngine: SearchEngine<{}> | null = null;
let inFlight: Promise<number> | null = null;

// eslint-disable-next-line @typescript-eslint/ban-types
const initializeNationwideCountEngine = (): SearchEngine<{}> => {
  const orgId =
    process.env.NEXT_PUBLIC_COVEO_ORG_ID || 'iqviafnaquintilesimsnonproduction12c6jth8d';

  const engine = buildSearchEngine({
    configuration: {
      organizationId: orgId,
      accessToken:
        process.env.NEXT_PUBLIC_COVEO_SITES_ACCESSTOKEN || 'xx7f5138ce-1ddd-4e63-a922-0730d9795d4b',
      organizationEndpoints: getOrganizationEndpoints(orgId),
      search: {
        searchHub: process.env.NEXT_PUBLIC_COVEO_SITES_SEARCHHUB || 'AvacareProtocolSitesDev',
      },
      analytics: {
        enabled: isCoveoAnalyticsEnabled(),
      },
    },
  });

  buildResultsPerPage(engine, {
    initialState: { numberOfResults: NATIONWIDE_RESULTS_PER_PAGE },
  });

  return engine;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const getNationwideCountEngine = (): SearchEngine<{}> => {
  if (!nationwideCountEngine) {
    nationwideCountEngine = initializeNationwideCountEngine();
  }

  return nationwideCountEngine;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const waitForTotalCount = (engine: SearchEngine<{}>): Promise<number> =>
  new Promise((resolve, reject) => {
    const previousRequestId = engine.state.search.requestId;
    let settled = false;
    let unsubscribe = (): void => undefined;

    const finish = (handler: () => void): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      handler();
    };

    unsubscribe = engine.subscribe(() => {
      const search = engine.state.search;
      if (search.requestId === previousRequestId) {
        return;
      }
      if (search.isLoading) {
        return;
      }

      if (search.error) {
        finish(() => {
          reject(search.error);
        });
        return;
      }

      finish(() => {
        resolve(search.response?.totalCountFiltered ?? 0);
      });
    });

    const timeoutId = setTimeout(() => {
      finish(() => {
        reject(new Error('Nationwide sites count search timed out'));
      });
    }, NATIONWIDE_SITES_COUNT_TIMEOUT_MS);
  });

const runNationwideCountSearch = (): Promise<number> => {
  const engine = getNationwideCountEngine();
  const { updateQuery } = loadQueryActions(engine);
  const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);
  const { executeSearch } = loadSearchActions(engine);
  const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);

  engine.dispatch(
    updateAdvancedSearchQueries({
      aq: buildSitesAdvancedQuery(),
    })
  );
  engine.dispatch(updateQuery({ q: '' }));

  const totalPromise = waitForTotalCount(engine);
  engine.dispatch(executeSearch(logInterfaceLoad()));
  return totalPromise;
};

export const fetchNationwideSitesCount = (): Promise<number> => {
  if (inFlight) {
    return inFlight;
  }

  inFlight = runNationwideCountSearch().finally(() => {
    inFlight = null;
  });

  return inFlight;
};
